import React from 'react';
import { LocationVerificationReport as LocationVerificationReportData } from '../../models/Report';
import { ExportDropdown } from './ExportDropdown';
import { rowsToCsv, downloadCsv, todayStamp } from '../../utils/csv';

interface Props {
  data: LocationVerificationReportData;
  onBack?: () => void;
}

export const LocationVerificationReport: React.FC<Props> = ({ data }) => {
  const handleExportCSV = () => {
    const csv = rowsToCsv(data.flaggedLocations, [
      { header: 'Date', get: (r) => r.date.toLocaleDateString() },
      { header: 'RBT', get: (r) => r.rbtName },
      { header: 'Client', get: (r) => r.clientName },
      { header: 'Scheduled Location', get: (r) => r.scheduledLocation },
      { header: 'Actual Location', get: (r) => r.actualLocation },
      { header: 'Distance (mi)', get: (r) => r.distance.toFixed(2) },
      { header: 'Status', get: (r) => r.status },
    ]);
    downloadCsv(`location-verification_flagged_${todayStamp()}.csv`, csv);
  };

  return (
  <div>
    {/* Toolbar */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--spacing-4)' }}>
      <ExportDropdown onExportCSV={handleExportCSV} disabled={data.flaggedLocations.length === 0} />
    </div>

    <div className="grid grid-cols-4" style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
      <div className="card">
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
          Verification Rate
        </div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-success)' }}>
          {data.verificationRate}%
        </div>
      </div>
      <div className="card">
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
          Total Check-ins
        </div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)' }}>
          {data.totalCheckIns}
        </div>
      </div>
      <div className="card">
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
          Failed Verifications
        </div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-error)' }}>
          {data.failedVerifications}
        </div>
      </div>
      <div className="card">
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
          Avg Deviation (mi)
        </div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)' }}>
          {data.averageDistanceDeviation}
        </div>
      </div>
    </div>

    {data.flaggedLocations.length > 0 && (
      <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
        <h3 style={{ marginBottom: 'var(--spacing-4)' }}>Flagged Locations</h3>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>RBT</th>
                <th>Client</th>
                <th>Distance (mi)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.flaggedLocations.map((flag, idx) => (
                <tr key={idx}>
                  <td>{flag.date.toLocaleDateString()}</td>
                  <td>{flag.rbtName}</td>
                  <td>{flag.clientName}</td>
                  <td>{flag.distance.toFixed(2)}</td>
                  <td>
                    <span
                      className={`badge badge-${
                        flag.status === 'pending'
                          ? 'warning'
                          : flag.status === 'approved'
                          ? 'success'
                          : 'error'
                      }`}
                    >
                      {flag.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="card">
      <h3 style={{ marginBottom: 'var(--spacing-4)' }}>By RBT</h3>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>RBT Name</th>
              <th>Total Check-ins</th>
              <th>Verified</th>
              <th>Failed</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.byRBT.map((rbt) => (
              <tr key={rbt.rbtId}>
                <td>{rbt.rbtName}</td>
                <td>{rbt.totalCheckIns}</td>
                <td>{rbt.verifiedCheckIns}</td>
                <td>{rbt.failedVerifications}</td>
                <td>
                  <span
                    style={{
                      color:
                        rbt.verificationRate >= 90
                          ? 'var(--color-success)'
                          : 'var(--color-warning)',
                    }}
                  >
                    {rbt.verificationRate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
};
